import type { CompanyInvoice } from '@tandemone/types'
import type { ColumnDef } from '@tanstack/react-table'
import { Download } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { extractErrorMessage } from '@/api/extract-error'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'

import { getInvoiceLinks, getSuperInvoiceLinks } from './api'
import { formatBillingDate } from './plan-price'
import { goToStripe, openStripeTab } from './stripe-tab'

export function formatAmount(cents: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency.toUpperCase()
  }).format(cents / 100)
}

/* settled green, owed amber, written off red, money back blue, and the two that
 * are nobody's problem grey. matches the tinted-badge pattern the rest of the
 * app uses, dark variants included. */
const TONES = {
  settled:
    'border-transparent bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200',
  owed: 'border-transparent bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200',
  lost: 'border-transparent bg-rose-100 text-rose-900 dark:bg-rose-950 dark:text-rose-200',
  credited:
    'border-transparent bg-sky-100 text-sky-900 dark:bg-sky-950 dark:text-sky-200',
  inert: 'border-transparent bg-muted text-muted-foreground'
} as const

export function InvoiceBadge({ invoice }: { invoice: CompanyInvoice }) {
  const { status } = invoice
  /* a credit note leaves status paid, so the refund would be invisible. */
  if (invoice.amountCreditedCents > 0 && status !== 'void') {
    const full = invoice.amountCreditedCents >= invoice.amountPaidCents
    return (
      <Badge className={TONES.credited}>
        {full ? 'Refunded' : 'Partly refunded'}
      </Badge>
    )
  }
  if (status === 'paid') return <Badge className={TONES.settled}>Paid</Badge>
  if (status === 'open') return <Badge className={TONES.owed}>Due</Badge>
  if (status === 'uncollectible') {
    return <Badge className={TONES.lost}>Unpaid</Badge>
  }
  /* draft is not issued yet, void was withdrawn. neither is owed, but they are
   * different enough that one outline for both read as the same thing. */
  if (status === 'draft') return <Badge variant='outline'>Draft</Badge>
  return <Badge className={TONES.inert}>Void</Badge>
}

/* fetched on click, not stored: stripe expires a hosted invoice url 30 days
 * after its due date and rotates the pdf link.
 *
 * companyId means super scope, and it has to change the endpoint as well as the
 * query: a super has no active company, so the company-scoped route answers
 * "Super scope has no company" and the receipt never opens. */
export function InvoiceLink({
  invoice,
  companyId
}: {
  invoice: CompanyInvoice
  companyId?: string
}) {
  const [busy, setBusy] = useState(false)

  async function open(tab: Window | null) {
    setBusy(true)
    try {
      const links = companyId
        ? await getSuperInvoiceLinks(companyId, invoice.id)
        : await getInvoiceLinks(invoice.id)
      const url =
        invoice.status === 'open'
          ? (links.hostedInvoiceUrl ?? links.invoicePdfUrl)
          : (links.invoicePdfUrl ?? links.hostedInvoiceUrl)
      if (!url) {
        tab?.close()
        toast.error('That invoice is no longer available from Stripe')
        return
      }
      goToStripe(url, tab)
    } catch (error) {
      tab?.close()
      toast.error(extractErrorMessage(error))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Button
      size='sm'
      disabled={busy}
      onClick={() => void open(openStripeTab())}
    >
      {invoice.status === 'open' ? (
        'Pay'
      ) : (
        <>
          <Download className='size-3.5' />
          Download
        </>
      )}
    </Button>
  )
}

/* a factory rather than a constant: the receipt cell needs to know whether it
 * is rendering for the owning company or for a super looking at one. */
export function invoiceColumns(
  companyId?: string
): ColumnDef<CompanyInvoice>[] {
  return [
    {
      id: 'number',
      meta: { width: '22%' },
      header: 'Invoice',
      enableSorting: false,
      cell: ({ row }) => (
        <span className='block truncate font-medium tabular-nums'>
          {row.original.number ?? '—'}
        </span>
      )
    },
    {
      id: 'createdAt',
      meta: { width: '130px' },
      header: 'Date',
      enableSorting: false,
      cell: ({ row }) => formatBillingDate(row.original.createdAt)
    },
    {
      id: 'amount',
      meta: { width: '120px' },
      header: 'Amount',
      enableSorting: false,
      cell: ({ row }) => (
        <span className='tabular-nums'>
          {formatAmount(row.original.amountDueCents, row.original.currency)}
        </span>
      )
    },
    {
      id: 'refunded',
      meta: { width: '120px' },
      header: 'Refunded',
      enableSorting: false,
      /* its own column rather than a smaller Amount: what they were charged and
         what came back are two facts, and netting them hides the refund. */
      cell: ({ row }) =>
        row.original.amountCreditedCents > 0 ? (
          <span className='tabular-nums text-sky-900'>
            −
            {formatAmount(
              row.original.amountCreditedCents,
              row.original.currency
            )}
          </span>
        ) : (
          <span className='text-ink-muted'>—</span>
        )
    },
    {
      id: 'status',
      meta: { width: '110px' },
      header: 'Status',
      enableSorting: false,
      cell: ({ row }) => <InvoiceBadge invoice={row.original} />
    },
    {
      id: 'paidAt',
      meta: { width: '130px' },
      header: 'Date paid',
      enableSorting: false,
      cell: ({ row }) => formatBillingDate(row.original.paidAt)
    },
    {
      id: 'receipt',
      header: 'Receipt',
      meta: { width: '130px', excludeFromRowLink: true },
      enableSorting: false,
      cell: ({ row }) => (
        <InvoiceLink invoice={row.original} companyId={companyId} />
      )
    }
  ]
}
